import React, { useState } from 'react';
import { Settings, X, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';

const WelcomeModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { canCreate } = usePermissions();
  const [dontShowAgain, setDontShowAgain] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    onClose(dontShowAgain);
  };

  const handleGoToSettings = () => {
    onClose(dontShowAgain);
    navigate('/settings');
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-auto animate-fade-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 bg-white rounded-full flex items-center justify-center">
                  <svg
                    className="h-6 w-6 text-primary-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
                    />
                  </svg>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Welcome to AdBooker!</h3>
                <p className="text-primary-100 text-sm">Let's get you started</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:text-primary-100 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {/* Important Notice */}
          <div className="mb-6 bg-amber-50 border-l-4 border-amber-400 p-4 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-amber-400" />
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-amber-800">Important Setup Required</h4>
                <div className="mt-2 text-sm text-amber-700">
                  <p>
                    As a new user, you need to add your{' '}
                    <strong>magazine details in Settings</strong>. You will not be able to add a
                    booking until this is done.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Getting Started Steps */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900">Quick Start Guide</h4>

            <div className="space-y-3">
              {/* Step 1 */}
              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                    1
                  </div>
                </div>
                <div className="flex-1">
                  <h5 className="font-medium text-gray-900">Set up your magazines</h5>
                  <p className="text-sm text-gray-600 mt-1">
                    Go to Settings and add your magazine publications with their details, issues,
                    and pricing.
                  </p>
                </div>
              </div>

              {/* Step 2 - Only show for users who can create customers */}
              {canCreate() && (
                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                      2
                    </div>
                  </div>
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">Add your customers</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      Navigate to Customers to create your client database with contact details and
                      categories.
                    </p>
                  </div>
                </div>
              )}

              {/* Step 3 - Adjust numbering based on whether customer step is shown */}
              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                    {canCreate() ? '3' : '2'}
                  </div>
                </div>
                <div className="flex-1">
                  <h5 className="font-medium text-gray-900">Start creating bookings</h5>
                  <p className="text-sm text-gray-600 mt-1">
                    Once magazines are set up, you can start creating advertising bookings for your
                    customers.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Don't show again checkbox */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <label className="flex items-center space-x-2 cursor-pointer group">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={e => setDontShowAgain(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
              />
              <span className="text-sm text-gray-600 group-hover:text-gray-900 select-none">
                Don't show this message again
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-between items-center">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
          >
            I'll do this later
          </button>
          <button
            onClick={handleGoToSettings}
            className="inline-flex items-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            <Settings className="h-4 w-4 mr-2" />
            Go to Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
